/**
 * Notifier — flash messages and modal popups, queued.
 *
 * Public API (preserved from the IIFE version):
 *   notifier.push({ type: 'flash' | 'modal', content, force?, elementType?,
 *                   onClick?, disableX?, name? }) → id
 *   notifier.dismiss(type?)  // 'flash' | 'modal'; defaults to whichever is active
 *
 * Cross-module compat shim — set on `window.notifier` so the surviving
 * IIFE callers (application.js noClick handler, sale-policy, hemming,
 * common-cart-controller, common-products-controller, mini-cart) keep
 * resolving. ESM callers (newsletter-subscribe, product template) also
 * already use `window.notifier` explicitly. Drop the shim in Phase 13.
 *
 * Migrated from theme/js/modules/notifier.js (IIFE → ESM, Phase 11).
 *
 * Queue events (changeEvent / dismissEvent) — the IIFE used jQuery
 * custom events on document for INTERNAL queue notifications.
 * Replaced with direct function calls (`onQueueChange`,
 * `onNotificationDismissed` are now invoked synchronously from push /
 * dismiss). No external code listened to them.
 *
 * Reads of `app.get('animations.modal.in')` and `body` / `header` refs
 * happen at push() / dismiss() time (after app.init() has hydrated),
 * so no module-eval init is needed.
 *
 * Source path: theme/js/modules/notifier.esm.js
 * Built name : ./notifier.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [Notifier]
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { fadeIn, fadeOut } from './core-anim.js';
import { saveLastFocused, restoreLastFocused } from './overlay.js';

const namespace = 'notifier:';
const changeEvent = namespace + 'change';
const dismissEvent = namespace + 'dismissed';
const flashActiveClass = 'flash-active';
const modalActiveClass = 'modal-active';
const flashContainerId = 'flash-container';
const flashContainerSelector = '#' + flashContainerId;
const flashSelector = '.js-flash-message';
const notifierDataAttr = 'data-notifier-id';
const modalSelector = '.js-modal-popup';
const closeModalSelector = '.js-close-modal';
const timeoutDuration = 5000;
const disableXClass = 'disable-x';

let queue = [];
let flashTimeout = null;
const flashClickHandlers = new Map();

// ─── Helpers ───────────────────────────────────────────────────────────────

function cloneTemplate(id) {
  const tpl = document.getElementById(id);
  if (!tpl || !tpl.content || !tpl.content.firstElementChild) return null;
  return tpl.content.firstElementChild.cloneNode(true);
}

function getBody() {
  // app.get('body') returns a jQuery collection wrapping <body>; we
  // use it raw via [0] from native callers, or fall back to <body>.
  const $body = appGet('body');
  if ($body && $body[0]) return $body[0];
  return document.body;
}

function getHeader() {
  const $header = appGet('header');
  if ($header && $header[0]) return $header[0];
  return document.querySelector('header');
}

function isFlashActive() {
  return appGet('isFlashActive') === true;
}

function isModalActive() {
  return appGet('isModalActive') === true;
}

function setFlashActive(value) {
  safeAppCall('set', 'isFlashActive', value);
  if (value) {
    document.documentElement.classList.add(flashActiveClass);
  } else {
    document.documentElement.classList.remove(flashActiveClass);
  }
}

function setModalActive(value) {
  safeAppCall('set', 'isModalActive', value);
  // Phase 13.2: native event consumers (no-click overlay coordinator
  // in application.esm.js) listen for `notifier:open` / `notifier:close`.
  document.dispatchEvent(new CustomEvent(value ? 'notifier:open' : 'notifier:close'));
  if (value) {
    document.documentElement.classList.add(modalActiveClass);
  } else {
    document.documentElement.classList.remove(modalActiveClass);
  }
}

// https://gist.github.com/gordonbrander/2230317
function ID() {
  return '_' + Math.random().toString(36).slice(2, 11);
}

function unsetFirstFromQueue() {
  queue.splice(0, 1);
}

// ─── Public — push / dismiss ───────────────────────────────────────────────

export function push(obj) {
  if (obj.type !== 'flash' && obj.type !== 'modal') return;
  obj.id = ID();
  if (obj.type === 'modal' && isFlashActive()) {
    obj.force = true;
  }
  if (obj.force === true) {
    queue.unshift(obj);
  } else {
    queue.push(obj);
  }
  log('[Notice] queued ' + changeEvent + ' (' + obj.type + ')');
  onQueueChange(queue, obj);
  return obj.id;
}

// Internal — removes a queued item by ID before it renders.
// Not exported (intentionally not part of the public API per the
// existing tests).
function pop(id) {
  for (let index = 0; index < queue.length; index++) {
    const obj = queue[index];
    if (obj.id === id) {
      queue.splice(index, 1);
      return;
    }
  }
}

export function dismiss(type) {
  if (type == null) {
    type = isModalActive() ? 'modal' : 'flash';
  }
  log('[Notice] ' + dismissEvent + ' (' + type + ')');
  onNotificationDismissed(type);
}

// ─── Queue lifecycle ───────────────────────────────────────────────────────

function onQueueChange(_queue, obj) {
  if (!isFlashActive() && !isModalActive() && _queue.length === 1) {
    render();
  } else if (obj.force === true) {
    replace();
  }
}

function onNotificationDismissed(type) {
  if (queue.length !== 0) {
    replace();
  } else {
    if (type === 'flash') {
      removeFlashMessage();
    } else if (type === 'modal') {
      removeModal();
    }
  }
}

function render() {
  const obj = queue[0];
  log('[Notice] ' + obj.type + (obj.name ? ' (' + obj.name + ')' : ''));
  if (obj.type === 'flash') {
    renderFlashMessage(obj);
  } else if (obj.type === 'modal') {
    renderModal(obj);
  }
  unsetFirstFromQueue();
}

function replace() {
  const obj = queue[0];
  if (isFlashActive() && obj.type === 'flash') {
    replaceFlashMessage(obj);
  } else {
    if (isFlashActive()) removeFlashMessage();
    // Synchronous removal during replace: the next modal is about to render and
    // both wrappers carry id="close-modal" + content carries id="ep-modal-heading".
    // Letting the old modal linger through fadeOut would create duplicate ids in
    // the DOM (and the new aria-labelledby would resolve to the wrong heading).
    else if (isModalActive()) removeModal({ sync: true });
    render();
  }
  unsetFirstFromQueue();
}

// ─── Flash messages ────────────────────────────────────────────────────────

function renderFlashMessage(obj) {
  const hasClickEvent = typeof obj.onClick === 'function';
  const body = getBody();
  let container = body && body.querySelector ? body.querySelector(flashContainerSelector) : null;
  const hasContainer = !!container;
  if (!hasContainer) {
    container = document.createElement('div');
    container.id = flashContainerId;
    container.setAttribute('aria-live', 'polite');
  }

  const tplEl = cloneTemplate('tpl-flash-message');
  if (!tplEl) return;
  tplEl.setAttribute(notifierDataAttr, obj.id);

  const elementType = typeof obj.elementType !== 'undefined' ? obj.elementType : 'notice';
  tplEl.setAttribute('data-type', elementType);
  tplEl.innerHTML = tplEl.innerHTML
    .replace('{content}', obj.content)
    .replace('{type}', elementType);

  if (hasClickEvent) {
    tplEl.setAttribute('tabindex', '0');
  }

  const animElement = hasContainer ? tplEl : container;
  animElement.style.display = 'none';

  if (!hasContainer) {
    container.appendChild(tplEl);
    const header = getHeader();
    if (header) header.appendChild(container);
    setFlashActive(true);
  } else {
    tplEl.style.display = 'none';
    container.appendChild(tplEl);
  }

  // Delegated click + keyup handler scoped to this notifier id. Stored
  // on the Map so removeFlashMessage / replaceFlashMessage can detach
  // it by id (matches the IIFE's $document.off('click keyup', '['+...+']')).
  const handler = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[' + notifierDataAttr + '="' + obj.id + '"]')) return;
    const canClick = hasClickEvent && event.type === 'click';
    const canPressEnter = hasClickEvent && event.type === 'keyup' && event.key === 'Enter';
    if (canClick || canPressEnter) {
      obj.onClick(event);
    }
    clearTimeout(flashTimeout);
    dismiss('flash');
  };
  document.addEventListener('click', handler);
  document.addEventListener('keyup', handler);
  flashClickHandlers.set(obj.id, handler);

  animElement.style.display = '';

  if (hasClickEvent) {
    setTimeout(() => { tplEl.focus(); }, 50);
  }

  flashTimeout = setTimeout(() => {
    dismiss('flash');
  }, timeoutDuration);
}

function replaceFlashMessage(obj) {
  const body = getBody();
  const container = body && body.querySelector ? body.querySelector(flashContainerSelector) : null;
  if (!container) return renderFlashMessage(obj);
  const activeFlash = Array.from(container.querySelectorAll(flashSelector))
    .find((el) => el.offsetParent !== null || el.style.display !== 'none');
  const activeId = activeFlash ? activeFlash.getAttribute(notifierDataAttr) : null;

  clearTimeout(flashTimeout);
  if (activeFlash) {
    activeFlash.style.display = 'none';
    activeFlash.remove();
  }
  if (activeId && flashClickHandlers.has(activeId)) {
    const h = flashClickHandlers.get(activeId);
    document.removeEventListener('click', h);
    document.removeEventListener('keyup', h);
    flashClickHandlers.delete(activeId);
  }
  renderFlashMessage(obj);
}

function removeFlashMessage() {
  const body = getBody();
  const container = body && body.querySelector ? body.querySelector(flashContainerSelector) : null;
  if (!container) return;
  const activeFlash = container.querySelector(flashSelector);
  const activeId = activeFlash ? activeFlash.getAttribute(notifierDataAttr) : null;
  clearTimeout(flashTimeout);
  if (activeId && flashClickHandlers.has(activeId)) {
    const h = flashClickHandlers.get(activeId);
    document.removeEventListener('click', h);
    document.removeEventListener('keyup', h);
    flashClickHandlers.delete(activeId);
  }
  container.style.display = 'none';
  container.remove();
  setFlashActive(false);
}

// ─── Modal popups ──────────────────────────────────────────────────────────

function renderModal(obj) {
  const tplEl = cloneTemplate('tpl-modal-popup');
  if (!tplEl) return;
  const modalInProps = appGet('animations.modal.in', { duration: 125 });

  tplEl.innerHTML = tplEl.innerHTML.replace('{content}', obj.content);

  if (obj.disableX === true) {
    tplEl.classList.add(disableXClass);
  }

  tplEl.style.display = 'none';
  const body = getBody();
  if (body) body.insertBefore(tplEl, body.firstChild);

  setModalActive(true);
  if (obj.name != null) {
    safeAppCall('set', 'modalActiveName', obj.name);
  }

  saveLastFocused();
  tplEl.style.display = 'flex';

  fadeIn(tplEl, { duration: modalInProps.duration, display: 'flex' }).then(() => {
    setTimeout(() => {
      const closeBtn = tplEl.querySelector(closeModalSelector);
      if (closeBtn) closeBtn.focus();
      safeAppCall('trapFocus', tplEl);
      tplEl.addEventListener('keyup', function escListener(event) {
        if (event.key === 'Escape') {
          tplEl.removeEventListener('keyup', escListener);
          closeModal(event);
        }
      }, { once: false });
    }, 50);
  });
}

function removeModal({ sync = false } = {}) {
  const body = getBody();
  const modal = body && body.querySelector ? body.querySelector(modalSelector) : null;
  const modalOutProps = appGet('animations.modal.out', { duration: 125 });
  restoreLastFocused();

  if (modal) {
    if (sync) {
      modal.remove();
    } else {
      fadeOut(modal, { duration: modalOutProps.duration }).then(() => {
        modal.remove();
      });
    }
  }
  setModalActive(false);
  safeAppCall('set', 'modalActiveName', '');
}

function closeModal(event) {
  event.preventDefault();
  dismiss('modal');
}

// ─── Document delegate — close modal click ────────────────────────────────

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const closeBtn = target.closest(closeModalSelector);
  if (closeBtn) closeModal(event);
});

// ─── Cross-module compat shim (retire in Phase 13) ────────────────────────

window.notifier = {
  push,
  dismiss,
};

log('[Notifier] registered');
